import { useState } from 'react';
import { formatDatum } from '../lib/aktivitaet.js';

/**
 * AGB-artiges Modal, das beim Login einmalig pro unbestätigtem Update
 * erscheint. Bewusst nicht über das generische <Modal> gebaut — der User
 * darf nicht per Klick außerhalb / Esc / X-Button entkommen, sondern muss
 * den Inhalt explizit bestätigen.
 *
 * Props:
 *   update      — der aktuell anzuzeigende Update-Record (titel, inhalt, …)
 *   verbleibend — wie viele Updates (inkl. dieses) sind noch offen
 *   onConfirm   — wird mit dem Update-Record aufgerufen, wenn bestätigt
 */
export default function UpdateGateModal({ update, verbleibend, onConfirm }) {
  const [busy, setBusy] = useState(false);

  if (!update) return null;

  async function handleConfirm() {
    if (busy) return;
    setBusy(true);
    try {
      await onConfirm(update);
    } finally {
      // busy wird nicht zurückgesetzt — der nächste Render bringt entweder
      // das nächste Update oder schließt das Modal komplett.
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/70" aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="update-gate-title"
        className="relative flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl"
      >
        <div className="flex flex-shrink-0 flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-6 py-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-semibold text-purple-800 ring-1 ring-inset ring-purple-200">
                Neu
              </span>
              {update.version && (
                <span className="text-xs font-medium text-slate-500">
                  Version {update.version}
                </span>
              )}
              {update.veroeffentlicht_am && (
                <span className="text-xs text-slate-400">
                  · {formatDatum(update.veroeffentlicht_am)}
                </span>
              )}
            </div>
            <h2
              id="update-gate-title"
              className="mt-1 text-lg font-semibold text-slate-900"
            >
              {update.titel}
            </h2>
          </div>
          {verbleibend > 1 && (
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
              noch {verbleibend} offen
            </span>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          <div
            className="update-content"
            dangerouslySetInnerHTML={{ __html: update.inhalt || '' }}
          />
        </div>

        <div className="flex flex-shrink-0 items-center justify-between border-t border-slate-200 bg-slate-50 px-6 py-4">
          <p className="text-xs text-slate-500">
            Wird nach Bestätigung nicht mehr angezeigt.
          </p>
          <button
            type="button"
            className="btn-primary"
            onClick={handleConfirm}
            disabled={busy}
          >
            {busy ? 'Speichere …' : 'Verstanden & bestätigen'}
          </button>
        </div>
      </div>
    </div>
  );
}
